/**
 * LlmTierSelector — lets the user pick an LLM tier (among admin-enabled tiers)
 * and, when the tier allows it, a model within it. Persisted per-user in
 * billing-service; the ai-orchestrator reads it at call time (egress via LiteLLM).
 */
import React, { useEffect, useMemo, useState } from 'react'
import { Layers, Loader2 } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useLlmTiers, useLlmPreference, useSetLlmPreference } from '@/services/llm-tier.service'
import { toast } from 'sonner'

export function LlmTierSelector() {
  const { data: tiers, isLoading } = useLlmTiers()
  const { data: pref } = useLlmPreference()
  const setPref = useSetLlmPreference()

  const [tier, setTier] = useState<string>('')
  const [model, setModel] = useState<string>('')

  useEffect(() => {
    if (pref?.tier && !tier) setTier(pref.tier)
    if (pref?.model_code && !model) setModel(pref.model_code)
  }, [pref]) // eslint-disable-line react-hooks/exhaustive-deps

  const current = useMemo(() => (tiers || []).find((t) => t.code === tier), [tiers, tier])

  // default tier/model once tiers load and nothing is selected yet
  useEffect(() => {
    if (!tier && tiers && tiers.length) {
      const t = tiers.find((x) => x.code === pref?.tier) || tiers[0]
      setTier(t.code)
      setModel(pref?.model_code || t.default_model_code || '')
    }
  }, [tiers]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = (nextTier: string, nextModel?: string) => {
    const t = (tiers || []).find((x) => x.code === nextTier)
    const m = nextModel || t?.default_model_code || undefined
    setPref.mutate(
      { tier: nextTier, model_code: m },
      {
        onSuccess: () => toast.success(`Niveau IA : ${t?.display_name}${m ? ` · ${m}` : ''}`),
        onError: (e: any) => toast.error('Choix refusé', { description: e?.response?.data?.detail || e?.message }),
      },
    )
  }

  const onTier = (v: string) => {
    setTier(v)
    const t = (tiers || []).find((x) => x.code === v)
    const m = t?.default_model_code || ''
    setModel(m)
    save(v, m)
  }
  const onModel = (v: string) => { setModel(v); save(tier, v) }

  if (isLoading) return <div className="flex items-center gap-1 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Niveaux…</div>
  if (!tiers || tiers.length === 0) return null

  const selModel = current?.models.find((m) => m.code === model)

  return (
    <div className="flex flex-col gap-1.5 w-full">
      <div className="flex items-center gap-2">
        <Layers className="h-3.5 w-3.5 text-violet-500 shrink-0" />
        <Select value={tier} onValueChange={onTier} disabled={setPref.isPending}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Niveau IA" /></SelectTrigger>
          <SelectContent>
            {tiers.map((t) => <SelectItem key={t.code} value={t.code} className="text-xs">{t.display_name}</SelectItem>)}
          </SelectContent>
        </Select>
        {current?.user_can_select_model && current.models.length > 1 && (
          <Select value={model} onValueChange={onModel} disabled={setPref.isPending}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Modèle" /></SelectTrigger>
            <SelectContent>
              {current.models.map((m) => <SelectItem key={m.code} value={m.code} className="text-xs">{m.display_name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      {selModel && (
        <span className="text-[10px] text-muted-foreground pl-5">
          {selModel.display_name} · {selModel.input_per_million === 0 ? 'gratuit' : `$${selModel.input_per_million}/$${selModel.output_per_million} par M tokens`}
          {!current?.user_can_select_model && ' · modèle fixé par l’admin'}
        </span>
      )}
    </div>
  )
}
